import React, { useCallback, useRef, useState } from "react";
import { GrAttachment } from "react-icons/gr";
import { RxCross2 } from "react-icons/rx";
import { MdError } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import { _allExtensions, _formatFileSize } from "../../../utils/common";
import toast from "react-hot-toast";

const SimpleFileUploader = ({
  label = "",
  placeholder = "Choose file",
  labelClass = "",
  required = false,
  id = "",
  limit = 5,
  onChange = () => null,
  error = false,
  accept = [],
  hidden = false,
  info = "JPG, PNG, or JPEG formats, max size is 100 MB each",
  multiple = true,
  value = [],
}) => {
  const [files, setFiles] = useState(value || []);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const _formatFiles = (files) => {
    return Array.from(files).map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      localpreview: URL.createObjectURL(file),
      loading: false,
      uuid: uuidv4(),
      // Don't store the File object in Redux state - it's not serializable
      // file: file, // Removed to fix Redux serialization warning
      info: "",
      accept: "",
    }));
  };

  const handleFileChange = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const localFiles = _formatFiles(e.target.files);

      // Validate files
      const validFiles = localFiles.filter((file) => {
        const extension = file.name.split(".").pop().toLowerCase();
        const isValidExtension = _allExtensions.includes(extension);
        const isAcceptedFormat = !accept.length || accept.includes(extension);
        
        if (!isValidExtension || !isAcceptedFormat) {
          file.loading = false;
          file.error = true;
          return false;
        }
        return true;
      });

      const invalidFiles = localFiles.filter((file) => file.error);

      if (files.length + validFiles.length > limit) {
        toast.error(`You can only upload up to ${limit} files.`);
        return;
      }

      if (!validFiles?.length) {
        if (accept?.length) {
          toast.error(`Please check the file format! Only the ${accept.join(", ")} formats are allowed.`);
        } else {
          toast.error("Please check the file format!");
        }
        return;
      }

      const newFiles = [...files, ...validFiles];
      setFiles(newFiles);
      onChange(newFiles);
    },
    [limit, files, accept, onChange]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const localFiles = _formatFiles(e.dataTransfer.files);
    
    // Use the same validation logic as handleFileChange
    const validFiles = localFiles.filter((file) => {
      const extension = file.name.split(".").pop().toLowerCase();
      const isValidExtension = _allExtensions.includes(extension);
      const isAcceptedFormat = !accept.length || accept.includes(extension);
      
      if (!isValidExtension || !isAcceptedFormat) {
        file.loading = false;
        file.error = true;
        return false;
      }
      return true;
    });

    if (files.length + validFiles.length > limit) {
      toast.error(`You can only upload up to ${limit} files.`);
      return;
    }

    if (!validFiles?.length) {
      if (accept?.length) {
        toast.error(`Please check the file format! Only the ${accept.join(", ")} formats are allowed.`);
      } else {
        toast.error("Please check the file format!");
      }
      return;
    }

    const newFiles = [...files, ...validFiles];
    setFiles(newFiles);
    onChange(newFiles);
  }, [files, accept, limit, onChange]);

  const removeFile = (uuid) => {
    const updatedFiles = files.filter((file) => file.uuid !== uuid);
    setFiles(updatedFiles);
    onChange(updatedFiles);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  if (hidden) return null;

  return (
    <div className="flex-col gap-1 relative flex">
      {label && (
        <div className="block">
          <label
            className={`text-theme font-medium text-sm ${labelClass}`}
            htmlFor={id}
          >
            {label}
            {required && <span className="text-red-600">*</span>}
          </label>
        </div>
      )}

      <div
        className={`border-2 border-dashed rounded-lg text-center w-full ${
          isDragging ? "border-theme bg-blue-50" : "border-theme bg-gray-100"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="fileInput"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileChange}
          placeholder={placeholder}
          multiple={multiple}
          accept={accept.join(",")}
        />
        
        {files?.length ? (
          <div className="cursor-pointer text-left items-start flex flex-row gap-4 p-4">
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] w-12 h-12 flex-center text-white rounded-md">
              <GrAttachment />
            </div>
            <div className="w-full text-sm flex flex-col">
              <span className="text-theme text-left text-sm font-bold">
                Attachments ({files?.length})
              </span>
              {files?.map((file) => (
                <div key={file?.uuid} className="flex flex-row justify-between items-center gap-6">
                  <div className="flex flex-row justify-between gap-2">
                    <span className="flex flex-row gap-2 items-center text-theme">
                      <GrAttachment />
                      <span className="truncate w-32">{file?.name}</span>
                    </span>
                    <span className="text-sm text-theme">
                      {_formatFileSize(file?.size)}
                    </span>
                  </div>
                  <span className="flex flex-row">
                    {file?.error ? (
                      <span className="text-red-500">
                        <MdError />
                      </span>
                    ) : null}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        removeFile(file?.uuid);
                      }}
                      className="text-red-500 hover:text-red-600"
                    >
                      <RxCross2 />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="cursor-pointer text-left flex flex-row gap-4 items-center p-4">
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] w-12 h-12 flex-center text-white rounded-md">
              <GrAttachment />
            </div>
            <div className="text-sm">
              <p className="text-theme">Drag and drop a file here.</p>
              <span className="text-textLight font-normal leading-none">
                {info}
              </span>
            </div>
          </div>
        )}
      </div>

      {error ? (
        <span className="flex items-center gap-1 text-sm text-error-600">
          <MdError />
          <p>{error}</p>
        </span>
      ) : null}
    </div>
  );
};

export default SimpleFileUploader;
